import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { PolaroidGarland } from './PolaroidGarland';
import { BookDecoration } from './BookDecoration';
import Scroll from 'src/game/components/shared/Scroll';
import StateObserver from 'src/StateObserver';
import {
  CurrentGenerationRecord,
  FamilyTreeRecord,
  FamilyTreeTile,
} from './FamilyTreeTile';
import * as History from 'src/replicant/history';
import * as Pet from 'src/replicant/pet';
import { findLastIndex } from 'src/replicant/utils/array';
import { createEmitter } from 'src/lib/Emitter';

import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = (closeMenu?: boolean) => void;

export default class PopupFamilyTree extends PopupBasic<CloseHandler> {
  private scrollView: Scroll<FamilyTreeRecord, FamilyTreeTile>;

  private data!: FamilyTreeRecord[];

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: opts.close,
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 1064,
      offsetY: 40,
      tracking: {
        feature: Feature.FAMILY._,
        $subFeature: Feature.FAMILY.TREE_POPUP._,
      },
      titleIcon: {
        image: 'assets/ui/family/icons/family_tree_icon.png',
        width: 60,
        height: 60,
      },
      boxOpts: {
        image: 'assets/ui/family/frames/family_popup_frame.png',
        scaleMethod: '9slice',
        sourceSlices: {
          horizontal: {
            left: 30,
            right: 30,
          },
          vertical: {
            top: 30,
            bottom: 30,
          },
        },
      } as const,
    });

    this.buttonClose.removeFromSuperview();

    this.root.addSubview(
      new PolaroidGarland({
        x: this.root.style.width * 0.48,
        y: 116 / 2,
      }),
      this.box,
    );

    new BookDecoration({ superview: this.box });

    this.box.addSubview(
      new ButtonScaleViewWithText({
        ...uiConfig.buttons.cancel,
        labelOffsetX: -4,
        labelOffsetY: -1,
        fontSize: 38,
        font: bitmapFonts('Body'),
        x: this.box.style.width / 2,
        y: this.box.style.height,
        width: 229,
        height: 80,
        centerOnOrigin: true,
        localeText: () => i18n('basic.back'),
        onClick: async () => {
          sounds.playSound('cancel');
          opts.close();
        },
        tracking: {
          feature: Feature.FAMILY._,
          $subFeature: Feature.FAMILY.TREE_POPUP.CLOSE,
        },
      }),
    );

    const scrollWidth = this.box.style.width * 0.82;
    const scrollHeight = this.box.style.height * 0.87;

    this.scrollView = new Scroll({
      superview: this.box,
      createItem: this.createItem.bind(this),
      recycle: true,
      setData: this.setData.bind(this),
      showBg: true,
      image: {
        image: 'assets/ui/family/frames/family_popup_list_frame.png',
        sourceSlices: {
          horizontal: {
            left: 30,
            right: 30,
          },
          vertical: {
            top: 30,
            bottom: 30,
          },
        },
      },
      rect: {
        x: 75,
        y: 75,
        width: scrollWidth,
        height: scrollHeight,
      },
    });

    // Already visitting. Don't add the emitter.
    if (StateObserver.getState().user.visit.visitSession) return;

    createEmitter(
      this.getView(),
      ({
        user: {
          visit: { visitSession },
        },
      }) => !!visitSession,
    ).addListener((visitSession) => {
      if (!visitSession) return;
      opts.close(true);
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/family/icons/family_tree_icon.png',
      'assets/ui/family/frames/family_popup_frame.png',
      'assets/ui/family/frames/family_popup_list_frame.png',
      'assets/ui/family/banners/garland.png',
    ]);
  }

  public init() {
    super.init({
      title: i18n('family.tree.title'),
    });

    const { user } = StateObserver.getState();

    const history = History.getters.getHistory(user);

    const { getPetName, getCurrentEvolution, getPetAge } = Pet.getters;
    const currentEvolution = getCurrentEvolution(user);
    const current: CurrentGenerationRecord | null = currentEvolution
      ? {
          type: 'current',
          name: getPetName(user),
          species: currentEvolution.species,
          age: getPetAge(user, StateObserver.now()),
          evolutions: user.pet.evolutions,
        }
      : null;
    const data = [
      ...(history.filter(
        (x) => x.type === 'duo' || x.type === 'death',
      ) as FamilyTreeRecord[]),
    ];
    if (current) data.push(current);
    this.data = data;
    this.scrollView.setItems(this.data);
    // Scroll to bottom.
    void this.scrollView.scrollTo(0, this.scrollView.scrollBounds.maxY, 0);
  }

  private createItem(superview: View, index: number, item: FamilyTreeRecord) {
    const tile = new FamilyTreeTile({
      superview,
    });

    this.setData(index, item, tile);

    return tile;
  }

  private setData(index: number, item: FamilyTreeRecord, view: FamilyTreeTile) {
    const lastDeathIndex =
      findLastIndex(this.data, (x, i) => x.type === 'death' && i < index) + 1;
    const generationIndex =
      lastDeathIndex === -1 ? index : index - lastDeathIndex;

    view.setData(generationIndex + 1, item);
  }
}
